import { DB_ERRORS } from "@fts/core";
import { ChecklistInvalidError, DataError, type DataErrorCode } from "../data/provider";
import type { MessageKey } from "../i18n";

/** One translation key per DataErrorCode (every DbError token plus the client-side codes). */
export const ERROR_KEYS: Record<DataErrorCode, MessageKey> = {
  invalid_credentials: "error.invalid_credentials",
  not_configured: "error.not_configured",
  network: "error.network",
  weak_password: "error.weak_password",
  same_password: "error.same_password",
  reauthentication_needed: "error.reauthentication_needed",
  not_in_demo: "error.not_in_demo",
  captcha_failed: "error.captcha_failed",
  invalid_code: "error.invalid_code",
  push_disabled: "error.push_disabled",
  invalid_push_subscription: "error.invalid_push_subscription",
  push_device_limit: "error.push_device_limit",
  unknown: "error.unknown",
  unauthorized: "error.unauthorized",
  bad_request: "error.bad_request",
  not_found: "error.not_found",
  broker_error: "error.broker_error",
  mail_error: "error.mail_error",
  forbidden: "error.forbidden",
  invalid_device_id: "error.invalid_device_id",
  invalid_imei: "error.invalid_imei",
  invalid_calibration: "error.invalid_calibration",
  device_exists: "error.device_exists",
  device_not_found: "error.device_not_found",
  device_revoked: "error.device_revoked",
  device_busy: "error.device_busy",
  vehicle_busy: "error.vehicle_busy",
  vehicle_inactive: "error.vehicle_inactive",
  tenant_mismatch: "error.tenant_mismatch",
  claim_invalid: "error.claim_invalid",
  invalid_email: "error.invalid_email",
  invalid_scope: "error.invalid_scope",
  already_member: "error.already_member",
  invitation_invalid: "error.invitation_invalid",
  wrong_email: "error.wrong_email",
  scope_changed: "error.scope_changed",
  site_is_last_scope: "error.site_is_last_scope",
  vehicle_has_history: "error.vehicle_has_history",
  rate_limited: "error.rate_limited",
  invalid_report_range: "error.invalid_report_range",
  report_too_large: "error.report_too_large",
  export_too_large: "error.export_too_large",
  card_in_use: "error.card_in_use",
  invalid_card: "error.invalid_card",
  invalid_permission: "error.invalid_permission",
  operator_left: "error.operator_left",
  invalid_left_on: "error.invalid_left_on",
  invalid_contact: "error.invalid_contact",
  invalid_alert_settings: "error.invalid_alert_settings",
  already_acknowledged: "error.already_acknowledged",
  note_too_long: "error.note_too_long",
  device_offline: "error.device_offline",
  too_many_live_watches: "error.too_many_live_watches",
  invalid_geofence: "error.invalid_geofence",
  too_many_geofences: "error.too_many_geofences",
  checklist_token_invalid: "error.checklist_token_invalid",
  already_approved: "error.already_approved",
  invalid_maintenance: "error.invalid_maintenance",
  invalid_form: "error.invalid_form",
  invalid_checklist: "error.invalid_checklist",
  invalid_photo: "error.invalid_photo",
  invalid_template: "error.invalid_template",
  invalid_shift: "error.invalid_shift",
  too_many_shifts: "error.too_many_shifts",
  invalid_recipient: "error.invalid_recipient",
  fault_resolved: "error.fault_resolved",
  form_changed: "error.form_changed",
  module_setting_locked: "error.module_setting_locked",
  invalid_dvr_settings: "error.invalid_dvr_settings",
};

// Compile-time guard is the Record type above; this keeps the list visibly in sync with core.
void DB_ERRORS;

export function errorCode(err: unknown): DataErrorCode {
  if (err instanceof ChecklistInvalidError) return "invalid_checklist";
  return err instanceof DataError ? err.code : "unknown";
}

/** Translation key for any error thrown by a DataProvider call. */
export function errorKey(err: unknown): MessageKey {
  return ERROR_KEYS[errorCode(err)];
}
